import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export interface AppNotification {
  id: string
  type?: 'success' | 'error' | 'info' | 'warning'
  title?: string
  message: string
  read: boolean
  timestamp: string
}

interface AppState {
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  notifications: AppNotification[]
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setTheme: (theme: 'light' | 'dark') => void
  addNotification: (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      sidebarOpen: false,
      theme: 'light',
      notifications: [],

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }), false, 'app/toggleSidebar')
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open }, false, 'app/setSidebarOpen')
      },

      setTheme: (theme: 'light' | 'dark') => {
        set({ theme }, false, 'app/setTheme')
        if (theme === 'dark') document.documentElement.classList.add('dark')
        else document.documentElement.classList.remove('dark')
      },

      addNotification: (notification) => {
        const newNotification: AppNotification = {
          ...notification,
          id: Date.now().toString(),
          timestamp: new Date().toISOString(),
          read: false,
        }
        set(
          (state) => ({ notifications: [...state.notifications, newNotification] }),
          false,
          'app/addNotification'
        )
        setTimeout(() => get().removeNotification(newNotification.id), 5000)
      },

      removeNotification: (id: string) => {
        set(
          (state) => ({ notifications: state.notifications.filter((n) => n.id !== id) }),
          false,
          'app/removeNotification'
        )
      },

      clearNotifications: () => set({ notifications: [] }, false, 'app/clearNotifications'),
    }),
    { name: 'app-store' }
  )
)
